import { useEffect, useState, useRef } from "react";
import {
  getLastRefreshTimeInMinutes,
  getManualRefreshTime,
  isDisableManualRefresh,
} from "utility";
import { getCalculatedLastRefreshTime } from "../utility/deviceDetailUtils";
import { useSelector } from "react-redux";
import { SYNC_STATUS } from "constants/syncStatus";

/**
 * Custom hook to manage manual refresh button state and timer
 * Matches React Native app implementation
 */
export const useDeviceManualRefresh = (deviceDetails, customSchemaForUI, selectedDeviceDetails, onButtonEnabled) => {
  const [disableManualRefresh, setDisableManualRefresh] = useState(false);
  const [manualRefreshIntervalTime, setManualRefreshIntervalTime] = useState(0);
  const [hasCalculated, setHasCalculated] = useState(false);
  const lastCalculatedTimeRef = useRef(null);
  const prevDisableStateRef = useRef(null);

  const {
    aneltoIntervals,
    freeusIntervals,
    clairvoyantIntervals,
    eViewIntervalArray,
  } = useSelector((store) => store?.devices);

  const checkManualRefreshButtonAccess = () => {
    if (!deviceDetails) return;

    // Early return for failed syncs (unless last two failed)
    if (
      deviceDetails?.syncHistory?.syncStatus === SYNC_STATUS.FAILED &&
      !deviceDetails?.syncHistory?.isLastTwoFailed
    ) {
      return;
    }

    if (selectedDeviceDetails) {
      const deviceType = selectedDeviceDetails?.device_type?.trim();
      if (deviceType) {
        const lastRequestedTime = customSchemaForUI
          ? customSchemaForUI().lastRequestedTime
          : getCalculatedLastRefreshTime(
              deviceDetails?.syncHistory?.syncRequestedOn,
              deviceDetails?.syncHistory?.syncCompletedOn
            );

        if (lastRequestedTime) {
          const manualRefreshTime = getManualRefreshTime(
            deviceType,
            aneltoIntervals,
            freeusIntervals,
            clairvoyantIntervals,
            eViewIntervalArray
          );
          const lastRefreshTime = customSchemaForUI
            ? customSchemaForUI().lastRefreshTime
            : deviceDetails?.syncHistory?.syncCompletedOn;

          const isDisable = isDisableManualRefresh(lastRefreshTime, manualRefreshTime);
          setDisableManualRefresh(isDisable);
          setHasCalculated(true);

          if (isDisable || deviceDetails?.syncHistory?.isLastTwoFailed) {
            const diffInSeconds = getLastRefreshTimeInMinutes(lastRefreshTime, true);
            const calculatedTime = manualRefreshTime * 60 - diffInSeconds;

            // Check if lastRefreshTime has changed (new sync completed)
            const lastRefreshTimeChanged =
              lastRefreshTime !== lastCalculatedTimeRef?.current;

            // Only recalculate timer if:
            // 1. No timer is currently running (manualRefreshIntervalTime is 0)
            // 2. OR lastRefreshTime has changed (new sync completed)
            // This prevents timer from jumping to incorrect values
            if (manualRefreshIntervalTime === 0 || lastRefreshTimeChanged) {
              if (calculatedTime >= 0) {
                setManualRefreshIntervalTime(calculatedTime);
                lastCalculatedTimeRef.current = lastRefreshTime;
              }
            }
            // If timer is already running and lastRefreshTime hasn't changed,
            // keep the current timer value (don't recalculate)
          } else {
            setManualRefreshIntervalTime(0);
            lastCalculatedTimeRef.current = null;
          }
        }
      }
    }
  };

  // Timer countdown for manual refresh
  useEffect(() => {
    if (!disableManualRefresh) return;
    const intervalId = setInterval(() => {
      setManualRefreshIntervalTime((prevTimer) => {
        if (prevTimer <= 0) {
          setDisableManualRefresh(false);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [disableManualRefresh, deviceDetails]);

  // Call API when button becomes enabled after timer expires
  useEffect(() => {
    // Check if button state changed from disabled (true) to enabled (false)
    if (prevDisableStateRef.current === true && disableManualRefresh === false) {
      // Button was just enabled after timer expired
      // Check if sync is still in-progress (fallback mechanism)
      const isStillInProgress = deviceDetails?.syncHistory?.syncStatus === SYNC_STATUS.IN_PROGRESS;
      
      if (onButtonEnabled && typeof onButtonEnabled === 'function') {
        onButtonEnabled(isStillInProgress);
      }
    }
    // Update previous state
    prevDisableStateRef.current = disableManualRefresh;
  }, [disableManualRefresh, onButtonEnabled, deviceDetails?.syncHistory?.syncStatus]);

  return {
    disableManualRefresh,
    manualRefreshIntervalTime,
    checkManualRefreshButtonAccess,
    hasCalculated,
  };
};

